// src/components/ReadjudicacionMasiva/Dashboard/LoteCard.js

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Chip,
    Button,
    Grid,
    Tooltip,
    LinearProgress
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const useStyles = makeStyles((theme) => ({
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
        }
    },
    cardContent: {
        flexGrow: 1,
        paddingBottom: theme.spacing(1)
    },
    estadoChip: {
        fontWeight: 600,
        fontSize: '0.75rem'
    },
    estadoAuditoria: {
        backgroundColor: '#fff3cd',
        color: '#856404'
    },
    estadoDesapoderamiento: {
        backgroundColor: '#d1ecf1',
        color: '#0c5460'
    },
    estadoAsignacion: {
        backgroundColor: '#d4edda',
        color: '#155724'
    },
    estadoFinalizado: {
        backgroundColor: '#f8d7da',
        color: '#721c24'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2)
    },
    loteId: {
        fontWeight: 700,
        fontSize: '1.25rem',
        color: theme.palette.primary.main
    },
    statsGrid: {
        marginTop: theme.spacing(2)
    },
    statItem: {
        textAlign: 'center',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius
    },
    statValue: {
        fontWeight: 700,
        fontSize: '1.5rem',
        lineHeight: 1
    },
    statLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5)
    },
    correctos: {
        color: theme.palette.success.main
    },
    defectuosos: {
        color: theme.palette.error.main
    },
    pendientes: {
        color: theme.palette.warning.main
    },
    excluidos: {
        color: theme.palette.grey[600]
    },
    progressBar: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        height: 8,
        borderRadius: 4
    },
    modulo: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1)
    }
}));

const LoteCard = ({ lote, onVer, onEditar }) => {
    const classes = useStyles();

    const getEstadoChip = (estado) => {
        const estados = {
            0: { label: 'Pendiente Auditoría', className: classes.estadoAuditoria },
            1: { label: 'Auditado', className: classes.estadoDesapoderamiento },
            2: { label: 'Desapoderado', className: classes.estadoAsignacion },
            3: { label: 'Asignado', className: classes.estadoAsignacion },
            4: { label: 'Listo para Pasaje', className: classes.estadoFinalizado },
            5: { label: 'Finalizado', className: classes.estadoFinalizado }
        };

        const estadoInfo = estados[estado] || { label: 'Desconocido', className: '' };

        return (
            <Chip
                label={estadoInfo.label}
                size="small"
                className={`${classes.estadoChip} ${estadoInfo.className}`}
            />
        );
    };

    const calcularProgreso = () => {
        const total = lote.TotalRegistros || 0;
        if (total === 0) return 0;
        const correctos = lote.Correctos || 0;
        return (correctos / total) * 100;
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '-';

        try {
            // Parsear formato .NET JSON: "/Date(1634785200000)/"
            const match = fecha.toString().match(/\/Date\((\d+)\)\//);
            if (match) {
                const timestamp = parseInt(match[1], 10);
                return format(new Date(timestamp), 'dd/MM/yyyy', { locale: es });
            }

            // Si no es formato .NET, intentar parsear normalmente
            return format(new Date(fecha), 'dd/MM/yyyy', { locale: es });
        } catch {
            return '-';
        }
    };

    return (
        <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
                <div className={classes.header}>
                    <Typography className={classes.loteId}>
                        Lote {lote.Id}
                    </Typography>
                    {getEstadoChip(lote.Estado)}
                </div>

                <Typography variant="body2" color="text.secondary">
                    Fecha: {formatearFecha(lote.Fecha)}
                </Typography>

                <Typography variant="body2" className={classes.modulo} noWrap>
                    {lote.Modulo}
                </Typography>

                <LinearProgress
                    variant="determinate"
                    value={calcularProgreso()}
                    className={classes.progressBar}
                    color="primary"
                />

                <Typography variant="caption" color="text.secondary">
                    Progreso: {calcularProgreso().toFixed(0)}%
                </Typography>

                <Grid container spacing={1} className={classes.statsGrid}>
                    <Grid size={3}>
                        <Tooltip title="Correctos">
                            <div className={classes.statItem}>
                                <CheckCircleIcon className={classes.correctos} fontSize="small" />
                                <Typography className={`${classes.statValue} ${classes.correctos}`}>
                                    {lote.Correctos || 0}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    OK
                                </Typography>
                            </div>
                        </Tooltip>
                    </Grid>

                    <Grid size={3}>
                        <Tooltip title="Defectuosos">
                            <div className={classes.statItem}>
                                <ErrorIcon className={classes.defectuosos} fontSize="small" />
                                <Typography className={`${classes.statValue} ${classes.defectuosos}`}>
                                    {lote.Defectuosos || 0}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    Error
                                </Typography>
                            </div>
                        </Tooltip>
                    </Grid>

                    <Grid size={3}>
                        <Tooltip title="Pendientes">
                            <div className={classes.statItem}>
                                <WarningIcon className={classes.pendientes} fontSize="small" />
                                <Typography className={`${classes.statValue} ${classes.pendientes}`}>
                                    {lote.Pendientes || 0}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    Pend
                                </Typography>
                            </div>
                        </Tooltip>
                    </Grid>

                    <Grid size={3}>
                        <Tooltip title="Excluidos">
                            <div className={classes.statItem}>
                                <RemoveCircleIcon className={classes.excluidos} fontSize="small" />
                                <Typography className={`${classes.statValue} ${classes.excluidos}`}>
                                    {lote.Excluidos || 0}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    Excl
                                </Typography>
                            </div>
                        </Tooltip>
                    </Grid>
                </Grid>
            </CardContent>

            <CardActions>
                <Button
                    size="small"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onVer && onVer(lote)}
                >
                    Ver
                </Button>
                <Button
                    size="small"
                    color="secondary"
                    startIcon={<EditIcon />}
                    onClick={() => onEditar && onEditar(lote)}
                >
                    Gestionar
                </Button>
            </CardActions>
        </Card>
    );
};

export default LoteCard;
